<template>
    <div>
      <div class="chart_holder" v-if="chartData && chartData.length">
        <plotly :data="chartData" :layout="chartLayout" :config="chartConfig"/>
      </div>
    </div>
  </template>
  
  <script>
  import { Plotly } from 'vue-plotly'
  import defaultLayout from '@/components/helpers/plotly_style'
  import ReportDataHelper from '@/components/helpers/ReportDataHelper'
  import moment from 'moment'
  
  export default {
    name: 'GaugeChartPlotly',
    components: {
      Plotly
    },
    data() {
      return {
        // Auto-populated from the parent
        statsData: null,
        liveData: null,
        related_ids: null,
        reportParams: null,
        report: null
      }
    },
    props: {
      /*
        'stats' or 'live'
        Tells whether we read from statsData or liveData.
      */
      data_source: {
        type: String,
        default: 'stats'
      },
      /*
        A single or array of stats. We'll only use the first if multiple.
      */
      statistics: {
        type: [String, Array],
        default: () => []
      },
      /*
        Aggregation mode for stats data: sum, max, min, count, etc.
        For live data, we usually just take the single numeric value per device, 
        or combine them. You can customize logic below as needed.
      */
      aggregration_mode: {
        type: String,
        default: 'sum'
      },
      /*
        If aggregator results in zero or near zero, optionally hide the chart or skip it.
      */
      hideIfZero: {
        type: Boolean,
        default: false
      },
      /*
        For stats data, we multiply final numeric values by this factor (e.g. convert liters → gallons).
      */
      value_multiple: {
        type: Number,
        default: 1
      },
      /*
        For live data, we read from liveData[data_key].
      */
      data_key: {
        type: String,
        default: 'devices'
      },
      /*
        If you want to combine multiple devices in live data, you can 
        do sum or max, etc. This code uses the same 'aggregration_mode' for live 
        if you want. See `_aggregateLiveValues()` for details.
      */
      null_values: {
        default: 0
      },
      /*
        The minimum and maximum for the gauge axis range.
      */
      minGauge: {
        type: Number,
        default: 0
      },
      maxGauge: {
        type: Number,
        default: 100
      },
      /*
        Standard layout (e.g. { title: 'Avg Fuel Efficiency' }).
      */
      layout: {
        type: Object,
        default: () => ({})
      },
      /*
        Plotly config (e.g. { responsive: true }).
      */
      config: {
        type: Object,
        default: () => ({ responsive: true })
      }
    },
    methods: {
      async getRequiredStats() {
        if (this.data_source === 'stats') {
          let stats = []
          if (this.statistics) {
            let newStats
            if (this.statistics instanceof Promise) {
              newStats = await this.statistics
            } else {
              newStats = this.statistics
            }
            stats = stats.concat(newStats).flat()
          }
          return stats
        }
        return []
      },
      async getRequiredDeviceProps() {
        // If live, we might want the statistic from each device
        if (this.data_source === 'live') {
          let fields = []
          if (this.statistics) {
            let newStats
            if (this.statistics instanceof Promise) {
              newStats = await this.statistics
            } else {
              newStats = this.statistics
            }
            fields = fields.concat(newStats).flat()
          }
          return fields
        }
        return []
      },
  
      // For stats data: aggregator across all devices and all date labels
      _aggregateStatsValue(statName) {
        if (!this.statsData || !this.statsData.stats || !this.statsData.stats[statName]) {
          return 0
        }
        const deviceDict = this.statsData.stats[statName]
        let resultVal = null
        // For each device
        Object.keys(deviceDict).forEach(deviceId => {
          const dateMap = deviceDict[deviceId]
          // For each date
          this.statsData.labels.forEach(lbl => {
            let rawVal = dateMap[lbl] == null ? this.null_values : dateMap[lbl]
            let val = rawVal * this.value_multiple
            if (resultVal === null) {
              resultVal = val
            } else {
              switch (this.aggregration_mode) {
                case 'sum':
                  resultVal += val
                  break
                case 'max':
                  resultVal = Math.max(resultVal, val)
                  break
                case 'min':
                  resultVal = Math.min(resultVal, val)
                  break
                case 'count':
                  resultVal += (val !== 0 ? 1 : 0)
                  break
                default:
                  resultVal += val
              }
            }
          })
        })
        return resultVal === null ? 0 : resultVal
      },
  
      // For live data: aggregator across all devices
      _aggregateLiveValues(statName) {
        if (!this.liveData || !this.liveData[this.data_key]) {
          return 0
        }
        const devDict = this.liveData[this.data_key]
        let resultVal = null
        Object.keys(devDict).forEach(devKey => {
          const devObj = devDict[devKey]
          let rawVal = ReportDataHelper.getNestedField(devObj, statName, this.null_values)
          let val = rawVal * this.value_multiple
          if (resultVal === null) {
            resultVal = val
          } else {
            switch (this.aggregration_mode) {
              case 'sum':
                resultVal += val
                break
              case 'max':
                resultVal = Math.max(resultVal, val)
                break
              case 'min':
                resultVal = Math.min(resultVal, val)
                break
              case 'count':
                resultVal += (val !== 0 ? 1 : 0)
                break
              default:
                resultVal += val
            }
          }
        })
        return resultVal === null ? 0 : resultVal
      }
    },
    computed: {
      chartData() {
        // 1) Pick the first statistic
        let statArr = Array.isArray(this.statistics) ? this.statistics : [this.statistics]
        if (!statArr.length) {
          return []
        }
        const primaryStat = statArr[0]
  
        // 2) Gather aggregator result
        let aggregatedValue = 0
        if (this.data_source === 'stats') {
          aggregatedValue = this._aggregateStatsValue(primaryStat)
        } else {
          aggregatedValue = this._aggregateLiveValues(primaryStat)
        }
  
        // 3) If hideIfZero and value is near zero, skip
        if (this.hideIfZero && Math.abs(aggregatedValue) < 1e-9) {
          return []
        }
  
        // 4) Build single gauge trace
        const trace = {
          type: 'indicator',
          mode: 'gauge+number',
          value: aggregatedValue,
          gauge: {
            axis: { range: [this.minGauge, this.maxGauge] },
            // Optional color or threshold logic
            // steps: [
            //   { range: [0, 30], color: "lightgreen" },
            //   { range: [30, 70], color: "yellow" },
            //   { range: [70, 100], color: "red" }
            // ],
          },
          // If you want a prefix or suffix, you can do:
          // number: { suffix: " mpg" }
          title: { text: primaryStat }
        }
        return [trace]
      },
      chartLayout() {
        let newLayout = {
          ...defaultLayout,
          ...this.layout
        }
  
        // Gauge charts typically do well with centered layout
        if (!newLayout.margin) {
          newLayout.margin = {}
        }
        if (!newLayout.margin.t) newLayout.margin.t = 50
        if (!newLayout.margin.b) newLayout.margin.b = 30
  
        // Optionally center the gauge
        if (!newLayout.width) {
          newLayout.width = 300
        }
        if (!newLayout.height) {
          newLayout.height = 300
        }
  
        return newLayout
      },
      chartConfig() {
        return this.config
      }
    }
  }
  </script>
  
  <style scoped>
  .chart_holder {
    width: 100%;
    height: 100%;
    min-height: 300px;
  }
  </style>
  